// (cmepaper-20261101) CME futures PAPER trading. Client-side only, never
// sends an order anywhere. One net position per root (no hedging). Fills at
// the chart's last trade price. PnL in USD = points x contracts x point value.
const KEY = 'cheddar_cme_paper_v1'
const QTY_KEY = 'cheddar_cme_paper_qty'
const HIST_MAX = 200
export const CME_PAPER_EVENT = 'solcheddar-cme-paper-changed'
export const CME_START_CASH = 10000
export const CME_MARGIN_PER_CT = 500   // flat, all roots for now

// $ per 1.0 point, per contract. Fallback 1.
const POINT_VALUE = { ES: 50, MES: 5, NQ: 20, MNQ: 2, YM: 5, MYM: 0.5, RTY: 50, M2K: 5, CL: 1000, MCL: 100, GC: 100, MGC: 10, SI: 5000, NG: 10000, ZN: 1000, ZB: 1000, ZF: 1000, ZT: 2000, RB: 42000, HO: 42000, HG: 25000, PL: 50, '6E': 125000, '6B': 62500, '6J': 12500000, '6C': 100000 }
export function pointValue(sym) {
  const s = String(sym || '').toUpperCase().replace(/[FGHJKMNQUVXZ]\d{1,2}$/, '')
  return POINT_VALUE[s] ?? POINT_VALUE[String(sym || '').toUpperCase()] ?? 1
}

function load() {
  try {
    const s = JSON.parse(localStorage.getItem(KEY) || 'null')
    if (s && typeof s === 'object') return { pos: s.pos || {}, hist: Array.isArray(s.hist) ? s.hist : [], realized: s.realized || {}, cash: Number.isFinite(s.cash) ? s.cash : CME_START_CASH }
  } catch {}
  return { pos: {}, hist: [], realized: {}, cash: CME_START_CASH }
}
function save(s) {
  try { localStorage.setItem(KEY, JSON.stringify(s)) } catch {}
  try { window.dispatchEvent(new CustomEvent(CME_PAPER_EVENT)) } catch {}
}

export function cmeQty() { const v = Number(localStorage.getItem(QTY_KEY)); return v >= 1 ? Math.round(v) : 1 }
export function setCmeQty(v) { try { localStorage.setItem(QTY_KEY, String(Math.max(1, Math.round(v) || 1))) } catch {} }

export function cmePosition(sym) { return (sym && load().pos[String(sym).toUpperCase()]) || null }
export function cmeRealized(sym) { return load().realized[String(sym || '').toUpperCase()] || 0 }
export function cmeHistory() { return load().hist }
export function cmeUnrealized(pos, px) {
  if (!pos || !(px > 0)) return 0
  return (px - pos.avg) * pos.qty * (pos.side === 'LONG' ? 1 : -1) * pointValue(pos.sym)
}

// side 'buy' | 'sell', qty contracts (>0). Nets against an opposite position;
// any excess flips. Returns { realized } for the fill.
export function cmeFill(sym, side, qty, px) {
  sym = String(sym || '').toUpperCase()
  qty = Math.round(qty)
  if (!sym || !(qty > 0) || !(px > 0)) throw new Error('no price yet')
  const s = load()
  const want = side === 'buy' ? 'LONG' : 'SHORT'
  const p = s.pos[sym]
  let realized = 0
  if (!p || p.side === want) {
    const q = (p?.qty || 0) + qty
    s.pos[sym] = { sym, side: want, qty: q, avg: p ? (p.avg * p.qty + px * qty) / q : px, t: p?.t || Date.now() }
  } else {
    const close = Math.min(p.qty, qty)
    realized = (px - p.avg) * close * (p.side === 'LONG' ? 1 : -1) * pointValue(sym)
    const left = p.qty - close
    const flip = qty - close
    if (left > 0) s.pos[sym] = { ...p, qty: left }
    else if (flip > 0) s.pos[sym] = { sym, side: want, qty: flip, avg: px, t: Date.now() }
    else delete s.pos[sym]
    s.realized[sym] = (s.realized[sym] || 0) + realized
    s.cash += realized
  }
  s.hist.unshift({ sym, side, qty, px, realized, t: Date.now() })
  if (s.hist.length > HIST_MAX) s.hist.length = HIST_MAX
  save(s)
  return { realized }
}
export function cmeFlatten(sym, px) {
  const p = cmePosition(sym)
  if (!p) return { realized: 0 }
  return cmeFill(sym, p.side === 'LONG' ? 'sell' : 'buy', p.qty, px)
}
export function cmeResetRealized(sym) { const s = load(); delete s.realized[String(sym || '').toUpperCase()]; save(s) }

// Account: cash (start + realized), equity (cash + open PnL at px for symPx's
// root; other roots at entry), margin used, available = equity - used.
export function cmeAccount(sym, px) {
  const s = load()
  let used = 0, upnl = 0
  for (const k in s.pos) {
    const p = s.pos[k]; used += p.qty * CME_MARGIN_PER_CT
    if (k === String(sym || '').toUpperCase()) upnl += cmeUnrealized(p, px)
  }
  const equity = s.cash + upnl
  return { cash: s.cash, equity, used, available: equity - used }
}
// Contracts that opening `side` would add would need margin; reductions are free.
export function cmeCanAfford(sym, side, qty, px) {
  const p = cmePosition(sym)
  const want = side === 'buy' ? 'LONG' : 'SHORT'
  const adds = !p || p.side === want ? qty : Math.max(0, qty - p.qty)
  return adds * CME_MARGIN_PER_CT <= cmeAccount(sym, px).available + 1e-9
}
export function cmeResetAccount() { save({ pos: {}, hist: [], realized: {}, cash: CME_START_CASH }) }
